import React, { useState, useEffect } from 'react';
import { XMarkIcon, EyeIcon, EyeSlashIcon, StarIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import useClickOutside from '../../hooks/useClickOutside';

interface Customer {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  date_of_birth: string | null;
  is_active: boolean;
  is_vip: boolean;
  vip_since: string | null;
  created_at: string;
  total_orders?: number;
  total_spent?: number;
  last_order_date?: string | null;
}

interface CustomerModalProps {
  customer: Customer | null;
  mode: 'create' | 'edit' | 'view';
  onClose: () => void;
  onSave: () => void;
}

interface FormData {
  name: string;
  email: string;
  password?: string;
  password_confirmation?: string;
  phone: string;
  date_of_birth: string;
  is_active: boolean;
  is_vip: boolean;
}

const CustomerModal: React.FC<CustomerModalProps> = ({ customer, mode, onClose, onSave }) => {
  const modalRef = useClickOutside(onClose);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    phone: '',
    date_of_birth: '',
    is_active: true,
    is_vip: false,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  useEffect(() => {
    if (customer && (mode === 'edit' || mode === 'view')) {
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        password: '',
        password_confirmation: '',
        phone: customer.phone || '',
        date_of_birth: customer.date_of_birth || '',
        is_active: customer.is_active,
        is_vip: customer.is_vip || false,
      });
    }
  }, [customer, mode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: []
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      let response;
      
      if (mode === 'create') {
        response = await apiService.post('/admin/customers', formData);
      } else if (mode === 'edit' && customer) {
        // Don't send password fields if they're empty
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
          delete updateData.password_confirmation;
        }
        response = await apiService.put(`/admin/customers/${customer.id}`, updateData);
      }

      if (response?.success) {
        onSave();
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({
          general: [error.response?.data?.message || 'An error occurred']
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return 'Add New Customer';
      case 'edit':
        return 'Edit Customer';
      case 'view':
        return 'Customer Details';
      default:
        return 'Customer';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div ref={modalRef} className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{getModalTitle()}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-black"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {mode === 'view' && customer ? (
          // View Mode
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <p className="text-sm text-gray-900">{customer.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="text-sm text-gray-900">{customer.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <p className="text-sm text-gray-900">{customer.phone || 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                <p className="text-sm text-gray-900">
                  {customer.date_of_birth ? formatDate(customer.date_of_birth) : 'Not provided'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    customer.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {customer.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
                <p className="text-sm text-gray-900">{formatDate(customer.created_at)}</p>
              </div>
            </div>

            {/* Customer Statistics */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Customer Statistics</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{customer.total_orders || 0}</div>
                  <div className="text-sm text-blue-800">Total Orders</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(customer.total_spent || 0)}
                  </div>
                  <div className="text-sm text-green-800">Total Spent</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm font-medium text-purple-600">Last Order</div>
                  <div className="text-sm text-purple-800">
                    {customer.last_order_date ? formatDate(customer.last_order_date) : 'No orders yet'}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          // Create/Edit Mode
          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                {errors.general[0]}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name[0]}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email[0]}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    errors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone[0]}</p>
                )}
              </div>

              <div>
                <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  id="date_of_birth"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    errors.date_of_birth ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.date_of_birth && (
                  <p className="mt-1 text-sm text-red-600">{errors.date_of_birth[0]}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password {mode === 'create' ? '*' : '(leave blank to keep current)'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 pr-10 border rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                      errors.password ? 'border-red-300' : 'border-gray-300'
                    }`}
                    required={mode === 'create'}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password[0]}</p>
                )}
              </div>

              <div>
                <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password {mode === 'create' ? '*' : ''}
                </label>
                <div className="relative">
                  <input
                    type={showPasswordConfirmation ? 'text' : 'password'}
                    id="password_confirmation"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 pr-10 border rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                      errors.password_confirmation ? 'border-red-300' : 'border-gray-300'
                    }`}
                    required={mode === 'create' || formData.password !== ''}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                  >
                    {showPasswordConfirmation ? (
                      <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password_confirmation && (
                  <p className="mt-1 text-sm text-red-600">{errors.password_confirmation[0]}</p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  Active Customer
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_vip"
                  name="is_vip"
                  checked={formData.is_vip}
                  onChange={handleChange}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                <label htmlFor="is_vip" className="ml-2 flex items-center text-sm text-gray-900">
                  <StarIcon className="w-4 h-4 text-yellow-500 mr-1" />
                  VIP Member
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {mode === 'create' ? 'Creating...' : 'Updating...'}
                  </div>
                ) : (
                  mode === 'create' ? 'Create Customer' : 'Update Customer'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomerModal;
